import { type FileUploadResponse, NapiClient } from '@adsk/napi-api-client'
import Compressor from 'compressorjs'

interface PixelCrop {
	x: number
	y: number
	width: number
	height: number
	aspect?: number
	unit?: 'px' | '%'
}
export const getNormalizedFile = async (file: File): Promise<File | Blob> => {
	return await new Promise((resolve, reject) => {
		return new Compressor(file, {
			maxWidth: 1000,
			maxHeight: 1000,
			success(normalizedFile) {
				resolve(normalizedFile)
			},
			error(error) {
				reject(error)
			},
		})
	})
}

const getFileContent = async (file: File): Promise<string> => {
	const reader = new FileReader()
	const normalizedFile = await getNormalizedFile(file)
	reader.readAsDataURL(normalizedFile)
	return await new Promise((resolve, reject) => {
		reader.onload = (f) => {
			resolve(f.target?.result as string)
		}
		reader.onerror = (err) => {
			reject(err)
		}
	})
}

const convertImageToFile = async (
	imageRef: HTMLImageElement,
	canvasRef: HTMLCanvasElement,
	pixelCrop: PixelCrop,
	fileName: string,
	fileType: string
): Promise<File> => {
	const blob = await imageToBlob(
		imageRef,
		canvasRef,
		pixelCrop,
		fileType ?? 'image/jpeg'
	)
	return blobToFile(blob, fileName)
}

const imageToBlob = async (
	imageRef: HTMLImageElement,
	canvasRef: HTMLCanvasElement,
	pixelCrop: PixelCrop,
	fileType: string
): Promise<Blob> => {
	return await new Promise((resolve, reject) => {
		const scaleX = imageRef.naturalWidth / imageRef.width
		const scaleY = imageRef.naturalHeight / imageRef.height
		const offscreen = document.createElement('canvas')
		offscreen.width = pixelCrop.width * scaleX
		offscreen.height = pixelCrop.height * scaleY
		const ctx = offscreen.getContext('2d')
		if (ctx === null || ctx === undefined) {
			reject(new Error('Failed to get canvas context'))
			return
		}
		ctx.drawImage(
			canvasRef,
			0,
			0,
			canvasRef.width,
			canvasRef.height,
			0,
			0,
			offscreen.width,
			offscreen.height
		)
		offscreen.toBlob(
			(blob) => {
				if (blob !== null) {
					resolve(blob)
				}
				reject(new Error('Failed to convert image to blob'))
			},
			fileType ?? 'image/jpeg',
			0.8
		)
	})
}

const blobToFile = (blob: Blob, fileName: string): File => {
	return new File([blob], fileName, { type: blob.type })
}

// Playing around the canvas for the preview
const setCropCanvas = async (
	image: HTMLImageElement,
	canvas: HTMLCanvasElement,
	crop: PixelCrop,
	mimeType: string = 'image/jpeg'
): Promise<void> => {
	if (crop.width === 0 || crop.height === 0) return
	const ctx = canvas.getContext('2d', {
		alpha: false,
	})
	if (ctx === null) return
	const scaleX = image.naturalWidth / image.width
	const scaleY = image.naturalHeight / image.height
	const pixelRatio = Math.min(2, window.devicePixelRatio)
	canvas.width = Math.floor(crop.width * scaleX * pixelRatio)
	canvas.height = Math.floor(crop.height * scaleY * pixelRatio)
	ctx.scale(pixelRatio, pixelRatio)
	ctx.imageSmoothingQuality = 'high'
	const cropX = crop.x * scaleX
	const cropY = crop.y * scaleY
	const centerX = image.naturalWidth / 2
	const centerY = image.naturalHeight / 2
	ctx.save()
	ctx.translate(-cropX, -cropY)
	// 4) Move the origin to the center of the original position
	ctx.translate(centerX, centerY)
	// 3) Rotate around the origin
	ctx.rotate(0)
	// 2) Scale the image
	ctx.scale(1, 1)
	// 1) Move the center of the image to the origin (0,0)
	ctx.translate(-centerX, -centerY)
	ctx.drawImage(
		image,
		0,
		0,
		image.naturalWidth,
		image.naturalHeight,
		0,
		0,
		image.naturalWidth,
		image.naturalHeight
	)
	ctx.restore()
}
const uploadFile = async ({
	objectName,
	contentType,
	binary,
}: {
	objectName: string
	contentType: string
	binary: File
}): Promise<FileUploadResponse> => {
	const fileUploadResponse = await NapiClient.uploadFile({
		objectName,
		contentType,
		binary,
	})
	return fileUploadResponse as unknown as FileUploadResponse
}

export default {
	convertImageToFile,
	setCropCanvas,
	getFileContent,
	uploadFile,
}
